import React, { useEffect, useState } from "react";
import Pusher from "pusher-js";
import { useRouter } from "next/router";
import {
  getLatestBidForAdmin_AuctionFloor,
  getAllBidsForAdmin_AuctionFloor,
  getAllWrongBidsForAdmin_AuctionFloor,
  getBidderSessionDataForAdmin_AuctionFloor,
} from "services";
import { DateTime } from "luxon";
import BankerEventData from "../banker/BankerEventData";
import { useSelector } from "react-redux";
import FloatingOnlineUsers from "./FloatingOnlineUsers";

export default function AdminAuctionFloor(props: any) {
  const [amount, setAmount] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [status, setStatus] = useState("");
  const [currentTime, setCurrentTime] = useState<any>(0);
  const [bidsEvent, setBidsEvent] = useState<any[]>([]);
  const [latestBid, setLatestBid] = useState<any>(null);
  const [prevBid, setPrevBid] = useState<any>(null);
  const [wrongBidsEvent, setWrongBidsEvent] = useState<any[]>([]);
  const [loading, setLoading] = useState<any>(true);
  const [onlineMembers, setOnlineMembers] = useState<any[]>([]);
  const [bidderSessionData, setBidderSessionData] = useState<any>(null);
  // const [connectionStatus, setConnectionStatus] = useState<any>("Offline");
  const [isOnlineEventTriggered, setIsOnlineEventTriggered] =
    useState<any>(false);

  const {
    query: { primary_identifier },
  } = useRouter();

  const router = useRouter();
  const accessToken = useSelector((state: any) => state.adminAuth.accessToken);

  // pusher functionality
  useEffect(() => {
    if (!primary_identifier) return;
    Pusher.logToConsole = false;
    let authConfig = {};
    let channelId = `auction-event-${primary_identifier}`;

    if (process.env.NEXT_PUBLIC_ENABLE_BIDDER_ACTIVITY === "true") {
      authConfig = {
        authEndpoint: `${process.env.NEXT_PUBLIC_AUCTION_FLOOR_URL}/pusher/auth`,
        auth: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            event_uuid: primary_identifier,
            user_type: "admin",
            user_name: "Admin",
          },
        },
      };
      channelId = `presence-${channelId}`;
    }
    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
      ...authConfig,
    });

    const channel = pusher.subscribe(channelId);

    channel.bind("bidding_value_has_been_changed", (data: any) => {
      handleDataChange(data);
      setBidsEvent((prev) => [...prev, data]);
    });

    channel.bind("user_placed_wrong_bid", (data: any) => {
      setWrongBidsEvent((prev) => [...prev, data]);
    });

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        setStatus("live");
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        setStatus("end");
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
      // setConnectionStatus("Offline");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
      // setConnectionStatus("Online");
    });

    //----------Pusher members---------
    if (process.env.NEXT_PUBLIC_ENABLE_BIDDER_ACTIVITY === "true") {
      // Set up presence events
      channel.bind("pusher:subscription_succeeded", (members: any) => {
        const memberList = Object.entries(members.members).map(
          ([id, member]: [string, any]) => ({
            id: id,
            name: member.name,
            user_type: member.user_type,
          }),
        );
        setOnlineMembers(memberList);
      });

      channel.bind("pusher:member_added", (member: any) => {
        setOnlineMembers((prev: any) => {
          // Check if member already exists to prevent duplicates
          const exists = prev.some((m: any) => m.id === member.id);
          if (exists) {
            return prev;
          }
          return [
            ...prev,
            {
              id: member.id,
              name: member.info.name,
              user_type: member.info.user_type,
            },
          ];
        });
      });

      channel.bind("pusher:member_removed", (member: any) => {
        setOnlineMembers((prev: any) =>
          prev.filter((m: any) => m.id !== member.id),
        );
      });
    }

    return function cleanup() {
      pusher.disconnect();
    };
  }, [primary_identifier]);

  // if auction is ongoing and somebody came in between then fetch data from latest bid
  useEffect(() => {
    if (props.data) {
      (async () => {
        const allBid: any = await getAllBidsForAdmin_AuctionFloor(
          props.data.uuid,
        );

        const allWrongBids: any = await getAllWrongBidsForAdmin_AuctionFloor(
          props.data.uuid,
        );

        if (allBid) {
          setBidsEvent(allBid);
          if (allBid.length >= 2) {
            setPrevBid(allBid[allBid.length - 2]);
          }
        }
        if (allWrongBids) {
          setWrongBidsEvent(allWrongBids);
        }
        const lastBid: any = await getLatestBidForAdmin_AuctionFloor(
          props.data.uuid,
        );
        setLatestBid(lastBid);
        setStatus(props.data.status);
        const start_timestamp = DateTime.fromISO(props.data.start_date_time)
          .setZone("Asia/Kolkata")
          .toMillis();
        setStartTime(start_timestamp);

        if (lastBid) {
          setAmount(lastBid.final_amount);
          setEndTime(parseInt(lastBid.final_end_time));
        } else {
          setAmount(parseInt(props.data.reserve_price));
          setEndTime(parseInt(props.data.final_end_date));
        }
        setLoading(false);
      })();
    }
  }, [props.data]);

  // current time
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(
        DateTime.now().setZone("Asia/Kolkata").toFormat("hh:mm:ss a"),
      );
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isOnlineEventTriggered) {
      router.reload();
    }
  }, [isOnlineEventTriggered]);

  const handleDataChange = async (data: any) => {
    setAmount(data.final_amount);
    setEndTime(data.final_end_time);
    if (latestBid) {
      setPrevBid(latestBid);
    }
    const lastBid: any = await getLatestBidForAdmin_AuctionFloor(
      data.event_uuid,
    );

    setLatestBid((prev: any) => {
      setPrevBid(prev);
      return lastBid;
    });
  };

  const getBidderSessionDataForAdministrator = async (id: any) => {
    try {
      const data = await getBidderSessionDataForAdmin_AuctionFloor(id);
      setBidderSessionData(data);
      return data;
    } catch (error) {
      console.error("Error fetching session data:", error);
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      if (
        process.env.NEXT_PUBLIC_ENABLE_BIDDER_ACTIVITY === "true" &&
        primary_identifier
      ) {
        await getBidderSessionDataForAdministrator(primary_identifier);
      }
    })();
  }, [primary_identifier, status]);

  return (
    <div className="rounded-lg">
      <div className="mb-8">
        {loading ? (
          <div
            role="status"
            className="py-4 space-y-4 border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse dark:divide-gray-700 md:p-6 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
                <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
              </div>
              <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
            </div>
            <div className="flex items-center justify-between pt-4">
              <div>
                <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
                <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
              </div>
              <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
            </div>
            <div className="flex items-center justify-between pt-4">
              <div>
                <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
                <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
              </div>
              <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
            </div>
            <div className="flex items-center justify-between pt-4">
              <div>
                <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
                <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
              </div>
              <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
            </div>
            <div className="flex items-center justify-between pt-4">
              <div>
                <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
                <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
              </div>
              <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
            </div>
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          <>
            <BankerEventData
              data={props.data}
              latestBidAmount={amount}
              startTime={startTime}
              endTime={endTime}
              status={status}
              setStatus={setStatus}
              currentTime={currentTime}
              latestBid={latestBid}
              prevBid={prevBid}
              correctBids={bidsEvent}
              wrongBids={wrongBidsEvent}
              // connectionStatus={connectionStatus}
            />
            {process.env.NEXT_PUBLIC_ENABLE_BIDDER_ACTIVITY === "true" && (
              // {/* Floating Online Users */}
              <FloatingOnlineUsers
                onlineMembers={onlineMembers}
                status={status}
                bidderSessionData={bidderSessionData}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
